import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Box} from "@mui/material";

import styles from "./Sports.module.css";
import {Loader, FilmLists} from "../../components";
import {
    CategoryResponse,
    useFetchCategoriesRequestQuery,
    useLazyGetNearbyEventsQuery,
    useLazyGetPageContentQuery,
} from "../../api/modules";
import {useUserRole} from "../../hooks/useUserRole";
import helper from "../../constants/helper";
import {
    SPORTS_LIVE_NOW_CATEGORY_ID,
    SPORTS_LIVE_NOW_CATEGORY_NAME,
    SPORTS_LIVE_NOW_STATUS,
} from "../../constants";

const PAGE_NAME = "SPORTS";
const INITIAL_PAGE = 1;
type CategoryPageInfo = Record<string, {page: number; hasMore: boolean}>;

const Sports = () => {
    const role = useUserRole();

    const PROFILE_ID = useMemo(
        () => Number(localStorage.getItem(helper.EPIC_PROFILE_ID)),
        []
    );

    const {
        data: pageData,
        isUninitialized,
        isFetching: isPageFetching,
        refetch,
    } = useFetchCategoriesRequestQuery({
        page: PAGE_NAME,
        type: "category_list",
        role,
    });

    const [triggerGetPageContent] = useLazyGetPageContentQuery();
    const [triggerGetNearbyEvents] = useLazyGetNearbyEventsQuery();

    const [allContents, setAllContents] = useState<any[][]>([]);
    const [categoryPageInfo, setCategoryPageInfo] = useState<CategoryPageInfo>({});
    const [isFetchingContent, setIsFetchingContent] = useState(false);

    const orderedPageData = useMemo(() => {
        if (!pageData?.length) return [];

        const sorted = [...pageData].sort((a, b) => {
            const firstOrder = typeof a.order === "number" ? a.order : Number.MAX_SAFE_INTEGER;
            const secondOrder = typeof b.order === "number" ? b.order : Number.MAX_SAFE_INTEGER;
            return firstOrder - secondOrder;
        });

        return sorted;
    }, [pageData]);

    const sportsSections = useMemo<CategoryResponse[]>(() => {
        return [
            {
                id: SPORTS_LIVE_NOW_CATEGORY_ID,
                name: SPORTS_LIVE_NOW_CATEGORY_NAME,
                order: 0,
                isVirtual: true,
            },
            ...orderedPageData,
        ];
    }, [orderedPageData]);

    const fetchAllContent = useCallback(
        async (opts?: { isRefresh?: boolean }) => {
            const isRefresh = Boolean(opts?.isRefresh);

            if (!sportsSections.length) return;

            // Only show full-screen loader on initial load
            if (!isRefresh) setIsFetchingContent(true);

            try {
                const results = await Promise.all(
                    sportsSections.map((item) =>
                        item.id === SPORTS_LIVE_NOW_CATEGORY_ID
                            ? triggerGetNearbyEvents({
                                page_number: INITIAL_PAGE,
                                rehydrated_pagination: true,
                                status: SPORTS_LIVE_NOW_STATUS,
                            }).unwrap()
                            : triggerGetPageContent({
                                page: PAGE_NAME,
                                role,
                                content_id: Number(item.id),
                                profile_id: PROFILE_ID,
                                page_number: INITIAL_PAGE,
                                rehydrated_pagination: true,
                            }).unwrap()
                    )
                );

                const mappedResults = results.map((r) => r?.results ?? []);
                const nextCategoryPageInfo = sportsSections.reduce<CategoryPageInfo>(
                    (acc, category, index) => {
                        acc[String(category.id)] = {
                            page: INITIAL_PAGE,
                            hasMore: Boolean(results[index]?.next),
                        };
                        return acc;
                    },
                    {}
                );
                setAllContents(mappedResults);
                setCategoryPageInfo(nextCategoryPageInfo);

            } catch (error) {
                console.error("Error fetching sports content:", error);
            } finally {
                setIsFetchingContent(false);
            }
        },
        [sportsSections, role, PROFILE_ID, triggerGetPageContent, triggerGetNearbyEvents]
    );


    const lastCategoriesKeyRef = useRef<string>("");

    useEffect(() => {
        if (!sportsSections.length) return;

        const key = sportsSections.map((c) => c.id).join(",");
        if (lastCategoriesKeyRef.current === key) return;
        lastCategoriesKeyRef.current = key;

        fetchAllContent({isRefresh: false});
    }, [sportsSections, fetchAllContent]);


    const refreshAll = useCallback(async () => {
        try {
            await refetch();
        } finally {
            await fetchAllContent({isRefresh: true});
        }
    }, [refetch, fetchAllContent]);

    const isAnyFetching = isPageFetching || isFetchingContent;

    if (isAnyFetching || !pageData) {
        return <Loader className={styles.loader}/>;
    }

    return (
            <Box className={styles.categorySpacing}>
                {/* Content Lists */}
                <FilmLists
                    refresh={refreshAll}
                    dataContent={sportsSections}
                    allContents={allContents}
                    setAllContents={setAllContents}
                    categoryPageInfo={categoryPageInfo}
                    setCategoryPageInfo={setCategoryPageInfo}
                    pageName={PAGE_NAME}
                    canRefresh={!isUninitialized}
                    isProgram={true}
                    isLoading={isPageFetching}
                />
            </Box>
    );
};

export default Sports;
